import * as React from 'react'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import TimelineOppositeContent, {
  timelineOppositeContentClasses,
} from '@mui/lab/TimelineOppositeContent'

export default function ProfessionalAchievements() {
  const achievements = [
    {
      company: 'Salesforce Inc., Dallas, TX',
      period: 'Dec 2023 – Present',
      details: [
        'Serve in a principal technical architecture capacity for strategic M&A integrations, architecting end-to-end data integration frameworks that unify acquired-company systems with Salesforce enterprise platforms.',
        'Designed scalable, secure, acquisition-ready integration architectures that helped accelerate post-acquisition integration timelines by up to 40%, enabling faster availability of seller, customer, revenue, and operational intelligence.',
        'Developed reusable data-mapping, reconciliation, and integration automation frameworks that reduced manual reconciliation effort by more than 70% and established standardized patterns for complex acquisition integrations.',
        'Architected scalable real-time enterprise data pipelines connecting previously siloed systems and improving availability of business-critical information used for seller productivity, revenue analysis, and strategic decision-making.',
        'Architected and delivered Corporate Development applications supporting evaluation and governance of transactions ranging from multi-million-dollar investments to multi-billion-dollar strategic acquisitions, centralizing financial, operational, and strategic intelligence.',
        'Transformed fragmented post-acquisition integration processes into scalable and repeatable enterprise capabilities, reducing integration risk, operational delays, data inconsistency, and disruption during high-risk organizational transitions.',
        'Led modernization of legacy integrations through MuleSoft-based architectures, improving scalability, reliability, maintainability, and enterprise integration governance.',
        'Designed automation capabilities for Corporate Development processes, including NDA management and approval workflows, reducing manual processing effort and improving governance and operational efficiency.'
      ],
    },
    {
      company: 'Wipro Limited, Dallas, TX',
      period: 'Mar 2013 – Oct 2023',
      subCompanies: [
        {
          name: 'Helmerich & Payne (H&P)',
          period: 'Mar 2023 – Oct 2023',
          details: [
            'Served in a critical architecture role supporting H&Ps enterprise technology landscape, providing technical and strategic leadership for business-critical ERP and integration initiatives.',
            'Led solution architecture and modernization planning across enterprise applications, working directly with business and technology stakeholders to translate complex operational requirements into scalable enterprise solutions.',
            'Provided architectural oversight for integrations, process improvements, and application enhancements supporting core business operations, while establishing design standards and technical direction for the delivery team.',
          ],
        },        
        {
          name: 'Ciena Corporation',
          period: 'May 2020 – Mar 2023',
          details: [
            'Led enterprise-scale digital modernization spanning cloud transformation, integration architecture, ERP processes, and intelligent automation',
            'Redesigned enterprise architecture to enable a cloud-first operating model, contributing to an approximately 40% reduction in infrastructure costs and approximately 20% improvement in system uptime.',
            'Implemented an AI-enabled automation framework using UiPath across procurement, finance, and supply-chain processes, eliminating manual dependencies and saving more than 15,000 work hours annually.',
            'Established a unified data strategy and integration model supporting real-time analytics, cross-functional visibility, and faster enterprise decision-making.',
          ],
        },
        {
          name: 'CVR Energy',
          period: 'Apr 2019 – May 2020',
          details: [
            'Optimized Enterprise Architecture: Consolidated disparate systems, achieving a 25% reduction in operational complexity and ensuring data consistency.',
            'Enhanced Asset Management: Improved inventory transparency across facilities, reducing recovery time by 20% and minimizing financial losses during operational disruptions.',
            'Streamlined Procurement Workflows: Automated and optimized procurement operations, increasing efficiency by 35% and reducing cycle times significantly.',
          ],
        },
        {
          name: 'Williams Energy',
          period: 'Mar 2013 – Apr 2019',
          details: [
            'Enabled Strategic Integration: Seamlessly integrated acquisitions into Oracle ERP systems, reducing onboarding time by 40% and aligning operations across entities.',
            'Modernized Financial Operations: Migrated Oracle 11i to R12, improving system reliability and delivering faster financial processing by 30%.',
            'Achieved Operational Excellence: Consolidated integrations under a unified framework, enhancing system compatibility and reducing support overhead by 20%.',
          ],
        },
      ],
    },
    {
      company: 'Stryker, San Jose, CA',
      period: 'Sep 2010 – Mar 2013',
      details: [
        'Redefined Integration Standards: Established a standardized architecture for all interfaces, reducing error rates by 50% and increasing scalability for future integrations.',
        'Transformed Invoice Management: Automated AP invoice approval workflows, cutting processing time by 40% and enhancing compliance with internal controls.',
        'Accelerated Global Expansion: Led the successful integration of acquired divisions into Oracle EBS R12, enabling rapid operational ramp-up and minimizing disruption.',
      ],
    },
    {
      company: 'IBM India',
      period: 'Jun 2009 – Sep 2010',
      details: [
        'Delivered Enterprise-Class Solutions: Spearheaded the deployment of scalable Oracle EBS solutions, aligning IT strategy with evolving business needs.',
        'Optimized Change Governance: Streamlined change management protocols, reducing project delays by 30% and improving stakeholder satisfaction.',
        'Elevated Team Performance: Mentored teams to achieve peak efficiency, fostering a high-performing environment that delivered consistent results.',
      ],
    },
    {
      company: 'NaviSite Inc., Gurgaon, India',
      period: 'Jul 2007 – Jun 2009',
      details: [
        'Transformed Transaction Ecosystems: Implemented EDI payment solutions, reducing transaction errors by 40% and accelerating payment cycles.',
        'Empowered Decision-Making: Delivered real-time sales order and inventory interfaces, enhancing visibility and driving quicker business decisions.',
        'Streamlined Payroll Operations: Integrated third-party payroll systems, reducing processing errors by 35% and enabling accurate reporting.',
        'Enhanced Data Analytics: Built custom BI views, providing actionable insights that improved operational decision-making.',
      ],
    },

    // Add more data here
  ]

  return (
    <section
      id="achivement"
      className="bg-gray-900 text-white py-16 px-6 md:px-20 lg:px-32"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section Title */}
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-12 tracking-widest text-blue-400">
          Professional Achievements
        </h2>

        <Timeline
          sx={{
            [`& .${timelineOppositeContentClasses.root}`]: {
              flex: 0.1,
            },
          }}
        >
          {achievements.map((data, index) => {
            return (
              <TimelineItem key={index}>
                <TimelineOppositeContent className="text-white">
                  {data.period}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <h1>{data.company}</h1>
                  <>
                    {data.subCompanies ? (
                      data.subCompanies.map((sub, idx) => {
                        return (
                          <div
                            key={idx}
                            // className="mt-4 pl-4 border-l-1 border-[#313c4d]"
                          >
                            <Timeline
                              sx={{
                                [`& .${timelineOppositeContentClasses.root}`]: {
                                  flex: 0.1,
                                },
                              }}
                            >
                              {/* <TimelineOppositeContent className="text-white">
                                {sub.period}
                              </TimelineOppositeContent> */}
                              <TimelineSeparator>
                                {/* <TimelineDot /> */}
                                <TimelineConnector />
                              </TimelineSeparator>
                              <TimelineContent>
                                <h3 className='flex  items-center gap-4'> <TimelineDot /> {sub.period}</h3>
                                <h1>{sub.name}</h1>
                                {/* <Timeline.Title className="text-blue-300">{subCompany.name}</Timeline.Title> */}
                                <ul className="list-disc pl-6 mt-2 ">
                                  {sub.details.map((detail, detailIndex) => (
                                    <li
                                      key={detailIndex}
                                      className="text-gray-300 "
                                    >
                                      {detail}
                                    </li>
                                  ))}
                                </ul>
                              </TimelineContent>
                            </Timeline>
                          </div>
                          // <p>fke</p>
                        )
                      })
                    ) : (
                      <ul className="list-disc pl-6 mt-2">
                        {data.details.map((det, idx) => {
                          return (
                            <li key={idx} className="text-gray-300">
                              {det}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </>
                </TimelineContent>
              </TimelineItem>
            )
          })}
        </Timeline>
      </div>
    </section>
  )
}
